import { assert } from 'ts-essentials';
import { SubChartType } from '@tencent/slide-sdk-pb-model';
import cloneDeep from 'lodash/cloneDeep';
import max from 'lodash/max';
import { shallowOmit } from '../../../../utils/object-utils';
import { ValuesDataSourceType } from '../../../../data/chart/sub-chart/sub-chart-shared-data';
import { egPlotSubChartTypeMapping } from '../set-sub-chart-mutation';
import type {
    IChartSerCustomData,
    IValuePointData,
    ICategory,
} from '../../../../data/chart/sub-chart/sub-chart-shared-data';
import type {
    ICTAxDataSource,
    ICTDLbls,
    ICTDPt,
    ICTNumData,
    ICTNumDataSource,
    ICTStrData,
    IEGAxisDelta,
    ICTScatterSer,
    ICTBubbleSer,
    IEGPlotDelta,
    ICTDLbl,
    ICTNumVal,
    ICTStrVal,
} from '@tencent/slide-sdk-pb-model';
import type { IPlotSubChart, IEgPlotProps } from '../set-sub-chart-mutation';
import type { ISubChartData, ISubChartSerData } from '../../../../data/chart/chart-data';

interface BaseSerData {
    val?: ICTNumDataSource;
    cat?: ICTAxDataSource;
    dPt?: ICTDPt[];
    dLbls?: ICTDLbls;
}

// 分类坐标数据
type CategoryData = ICTStrData | ICTNumData | undefined;
// 值坐标数据
type ValueData = ICTNumData | undefined;

export type IPlotChartData = IEGPlotDelta[keyof Omit<IEGPlotDelta, 'chartType'>];

export class SubChart {
    /**
     * 标准化SubChart数据，从mutation中的plotChartData，标准化成sdkModel中使用的subChartData
     */
    public toSdkSubChart<Data extends ISubChartData, PlotChart extends IPlotSubChart['props']>(
        type: SubChartType,
        plotChart: PlotChart,
        axesAddIndex?: number,
        axes?: IEGAxisDelta[],
    ): Data {
        assert(plotChart);

        return {
            ...plotChart,
            type,
            axesAddIndex,
            axes,
            ser: this.normalizeSeries(type, plotChart.ser as BaseSerData[]),
        } as unknown as Data; // 这个类型实在太难处理了，先 as unknown
    }

    /**
     * 将sdkModel使用的subChartData转换为mutation中的PlotChartData
     */
    public toMutationPlotChart(subChartData: ISubChartData): IEgPlotProps[keyof IEgPlotProps] {
        const subChart = cloneDeep(subChartData);
        return {
            ...shallowOmit(subChart, 'type', 'axes'),
            // eslint-disable-next-line complexity
            ser: subChart.ser?.map((serItem) => {
                const serCategoryPoints: Array<ICTStrVal | ICTNumVal> = [];
                const serValuePoints: Array<ICTStrVal | ICTNumVal> = [];
                const serDataPoints: ICTDPt[] = [];
                const serDataLabels: ICTDLbl[] = [];

                serItem.categories.forEach((category, index) => {
                    if (category.categoryPoint !== undefined) {
                        serCategoryPoints.push({
                            idx: index,
                            ...category.categoryPoint,
                        });
                    }

                    if (category.valuePoint !== undefined) {
                        serValuePoints.push({
                            idx: index,
                            ...category.valuePoint,
                        });
                    }

                    if (category.dataPoint !== undefined) {
                        serDataPoints.push({
                            ...category.dataPoint,
                            idx: { val: index },
                        });
                    }

                    if (category.dataLabel !== undefined) {
                        serDataLabels.push({
                            idx: { val: index },
                            ...category.dataLabel,
                        });
                    }
                });

                if (serItem.otherDPt !== undefined) {
                    serDataPoints.push({
                        ...serItem.otherDPt,
                        idx: { val: serItem.categories.length },
                    });
                }

                if (serItem.otherDLbl !== undefined) {
                    serDataLabels.push({
                        ...serItem.otherDLbl,
                        idx: { val: serItem.categories.length },
                    });
                }

                let cat: ICTAxDataSource | undefined = undefined;
                if (serCategoryPoints.length > 0) {
                    const ptCount = { val: serItem.categories.length };
                    const pt = serCategoryPoints;
                    const formatCode = serItem.categoryAxisData?.formatCode;

                    switch (serItem.categoryAxisData?.type) {
                        case ValuesDataSourceType.stringLiteral:
                            cat = {
                                strLit: {
                                    ptCount,
                                    pt,
                                },
                            };
                            break;
                        case ValuesDataSourceType.numberRef:
                            cat = {
                                numRef: {
                                    numCache: {
                                        ptCount,
                                        pt,
                                        formatCode,
                                    },
                                },
                            };
                            break;
                        case ValuesDataSourceType.numberLiteral:
                            cat = {
                                numLit: {
                                    ptCount,
                                    pt,
                                    formatCode,
                                },
                            };
                            break;
                        default:
                            // ValuesDataSourceType.stringRef:
                            cat = {
                                strRef: {
                                    strCache: {
                                        ptCount,
                                        pt,
                                    },
                                },
                            };
                            break;
                    }
                }

                let val: ICTNumDataSource | undefined = undefined;
                if (serValuePoints.length > 0) {
                    const ptCount = { val: serItem.valueCount ?? serValuePoints.length };
                    const pt = serValuePoints;
                    const formatCode = serItem.values?.formatCode;

                    switch (serItem.values?.type) {
                        case ValuesDataSourceType.numberLiteral:
                            val = {
                                numLit: {
                                    ptCount,
                                    pt,
                                    formatCode,
                                },
                            };
                            break;
                        default:
                            // ValuesDataSourceType.numberRef:
                            val = {
                                numRef: {
                                    numCache: {
                                        ptCount,
                                        pt,
                                        formatCode,
                                    },
                                },
                            };
                            break;
                    }
                } else if (serItem.disable) {
                    if (serItem.values?.type === ValuesDataSourceType.numberRef) {
                        val = {
                            numRef: {},
                        };
                    }
                }

                let dPt: ICTDPt[] | undefined = undefined;
                if (serDataPoints.length > 0) {
                    dPt = serDataPoints;
                }

                let dLbls: ICTDLbls | undefined = undefined;
                if ((serItem as any).dLbls) {
                    dLbls = {
                        ...(serItem as any).dLbls,
                        dLbl: serDataLabels.length > 0 ? serDataLabels : undefined,
                    };
                }

                const isMultiValAxis = this.isMultiValAxis(subChart.type);
                return {
                    ...shallowOmit(
                        serItem,
                        'categories',
                        'disable',
                        'valueCount',
                        'categoryAxisData',
                        'values',
                        'otherDPt',
                        'otherDLbl',
                    ),
                    ...(isMultiValAxis ? { xVal: cat } : { cat }),
                    ...(isMultiValAxis ? { yVal: val } : { val }),
                    dPt,
                    dLbls,
                };
            }),
        };
    }

    public createEgPlotBySubChart(subChart: ISubChartData): IEgPlotProps {
        const { type } = subChart;

        const plotKey = Object.keys(egPlotSubChartTypeMapping).find(
            (key) => egPlotSubChartTypeMapping[key as keyof IEgPlotProps] === type,
        );

        if (plotKey) {
            return {
                /**
                 * 3.0的数据基于mutation，为了方便前端编辑，做过一次ser下多属性的合并（2.0在导入导出做的标准化和还原）。
                 * 这里需要重新把3.0的 sdkData 还原为原本mutation中的的数据结构
                 */
                [plotKey]: this.toMutationPlotChart(subChart),
            };
        }

        return {};
    }

    /**
     * 标准化系列数据
     */
    protected normalizeSeries(type: SubChartType, serList?: BaseSerData[]): ISubChartSerData[] | undefined {
        return serList?.map((ser) => {
            const categories = this.mergeMultipleDataListToCategories(ser, type);
            const cat = this.getSerCat(ser, type);
            const val = this.getSerVal(ser, type);

            return {
                ...shallowOmit(ser, 'cat', 'val', 'dPt', 'xVal', 'yVal'),
                categories,
                disable: this.getSerDisabledStatus(ser, type),
                valueCount: this.getSerValueCount(ser, type),
                dLbls: ser.dLbls ? shallowOmit(ser.dLbls, 'dLbl') : ser.dLbls,
                categoryAxisData: cat ? this.getDataResourceDefaultSetting(cat) : undefined,
                values: val ? this.getDataResourceDefaultSetting(val) : undefined,
                otherDPt: this.getOtherDPt(ser, categories),
                otherDLbl: this.getOtherDLbl(ser, categories),
            };
        });
    }

    /**
     * 值坐标轴数据
     */
    protected getSerVal(ser: BaseSerData, type: SubChartType): ICTNumDataSource | undefined {
        if (this.isMultiValAxis(type)) {
            return (ser as ICTScatterSer | ICTBubbleSer).yVal;
        }

        return ser.val;
    }

    /**
     * 分类坐标轴数据
     */
    protected getSerCat(ser: BaseSerData, type: SubChartType): ICTAxDataSource | undefined {
        if (this.isMultiValAxis(type)) {
            return (ser as ICTScatterSer | ICTBubbleSer).xVal;
        }

        return ser.cat;
    }

    /**
     * DataPoint数据
     */
    protected getSerDPt(ser: BaseSerData): ICTDPt[] | undefined {
        return ser.dPt;
    }

    /**
     * 数据标签数据
     */
    protected getSerDLbls(ser: BaseSerData): ICTDLbls | undefined {
        return ser.dLbls;
    }

    /**
     * 是否为无效系列
     */
    protected getSerDisabledStatus(ser: BaseSerData, type: SubChartType): boolean {
        const serVal = this.getSerVal(ser, type);
        const values = this.getValuesFromNumberDataSource(serVal);

        // 如果有val数据，但是没有numberCache（values解析出来是空），则判定为无效series
        return !!(serVal && !values);
    }

    protected getSerValueCount(ser: BaseSerData, type: SubChartType): number | undefined {
        const values = this.getValuesFromNumberDataSource(this.getSerVal(ser, type));

        return this.getCountWithValuesData(values);
    }

    private isMultiValAxis(type: SubChartType) {
        return type === SubChartType.ScatterChart || type === SubChartType.BubbleChart;
    }

    /**
     * 将多个数组，通过idx关联，合并为一个数组，方便管理
     */
    private mergeMultipleDataListToCategories(ser: BaseSerData, type: SubChartType): IChartSerCustomData['categories'] {
        const val = this.getSerVal(ser, type);
        const cat = this.getSerCat(ser, type);
        const dPt = this.getSerDPt(ser);
        const dLbls = this.getSerDLbls(ser);

        const { count, catData, valData } = this.getCategoryCount(cat, val);

        const categories: IChartSerCustomData['categories'] = [];

        for (let i = 0; i < count; i++) {
            const dataLabel = dLbls?.dLbl?.find((dataLabel) => dataLabel.idx?.val === i);
            const dataPoint = dPt?.find((point) => point.idx?.val === i);
            categories.push({
                idx: i,
                categoryPoint: this.getValuePoint(catData, i),
                valuePoint: this.getValuePoint(valData, i),
                dataLabel: dataLabel ? shallowOmit(dataLabel, 'idx') : dataLabel,
                dataPoint: dataPoint ? shallowOmit(dataPoint, 'idx') : dataPoint,
            });
        }

        return categories;
    }

    /**
     * 从 ICTStrData 或者 ICTNumData 中获取对应索引的数据点
     */
    private getValuePoint(valuesData: CategoryData | ValueData, index: number): IValuePointData | undefined {
        const point = valuesData?.pt?.find((point) => point.idx === index);

        if (point) {
            return shallowOmit(point, 'idx');
        }

        return point;
    }

    /**
     * 获取系列中类别数
     */
    private getCategoryCount(
        cat?: ICTAxDataSource,
        val?: ICTNumDataSource,
    ): { count: number; catData: CategoryData; valData: ValueData } {
        const catValuesData = this.getValuesFromAxisDataSource(cat);
        const valValuesData = this.getValuesFromNumberDataSource(val);

        return {
            count: Math.max(this.getCountWithValuesData(catValuesData), this.getCountWithValuesData(valValuesData)),
            catData: catValuesData,
            valData: valValuesData,
        };
    }

    private getCountWithValuesData(values?: CategoryData | ValueData): number {
        if (!values) {
            return 0;
        }
        const { ptCount, pt } = values;
        const idxArr = pt?.map((point) => point.idx || 0) || [];
        // Math.max(...[]) -Infinity
        // max([]) undefined
        const maxPtIndex = max<number>(idxArr) ?? -Infinity;

        return Math.max(ptCount?.val || 0, maxPtIndex);
    }

    private getValuesFromAxisDataSource(axisData?: ICTAxDataSource): CategoryData {
        if (!axisData) {
            return;
        }

        return axisData.numRef?.numCache || axisData.strRef?.strCache || axisData.numLit || axisData.strLit;
    }

    private getValuesFromNumberDataSource(numberData?: ICTNumDataSource): ValueData {
        if (!numberData) {
            return;
        }

        return numberData.numRef?.numCache || numberData.numLit;
    }

    private getDataResourceDefaultSetting(values: ICTNumDataSource | ICTAxDataSource): {
        type: ValuesDataSourceType;
        formatCode?: string;
    } {
        if (values.numRef) {
            return {
                type: ValuesDataSourceType.numberRef,
                formatCode: values.numRef.numCache?.formatCode,
            };
        }

        if (values.numLit) {
            return {
                type: ValuesDataSourceType.numberLiteral,
                formatCode: values.numLit.formatCode,
            };
        }

        if ((values as ICTAxDataSource).strRef) {
            return {
                type: ValuesDataSourceType.stringRef,
            };
        }

        if ((values as ICTAxDataSource).strLit) {
            return {
                type: ValuesDataSourceType.stringLiteral,
            };
        }

        return {
            type: ValuesDataSourceType.stringRef,
        };
    }

    private getOtherDPt(ser: BaseSerData, categories: ICategory[]): ICTDPt | undefined {
        // 获取超出categories索引外的dPt
        const otherDPtIndex = categories.length;
        const otherDPt = this.getSerDPt(ser)?.find((item) => item.idx?.val === otherDPtIndex);
        return otherDPt ? shallowOmit(otherDPt, 'idx') : otherDPt;
    }

    private getOtherDLbl(ser: BaseSerData, categories: ICategory[]): ICTDLbl | undefined {
        // 获取超出categories索引外的DLbl
        const otherDLblIndex = categories.length;
        const otherDLbl = this.getSerDLbls(ser)?.dLbl?.find((item) => item.idx?.val === otherDLblIndex);
        return otherDLbl ? shallowOmit(otherDLbl, 'idx') : otherDLbl;
    }
}
